import { Reply, Search, Send, Smile, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { MAX_CHAT_MESSAGE_LENGTH } from "./ChatProvider";
import type { ChatMessage } from "./types";

const EMOJI_GROUPS = [
  {
    label: "Smileys",
    icon: "😀",
    keywords: "smile happy laugh cool party sad",
    emojis: [
      "😀",
      "😃",
      "😄",
      "😁",
      "😂",
      "🤣",
      "😊",
      "😍",
      "🥳",
      "😎",
      "🤩",
      "😭",
    ],
  },
  {
    label: "Gestures",
    icon: "👍",
    keywords: "hand clap thanks eyes strong",
    emojis: [
      "👍",
      "👎",
      "👏",
      "🙌",
      "🤝",
      "🙏",
      "💪",
      "👀",
      "🫡",
      "✌️",
      "🤞",
      "💯",
    ],
  },
  {
    label: "Racing",
    icon: "🏇",
    keywords: "horse race win trophy fire rocket casino money",
    emojis: [
      "🏇",
      "🐎",
      "🏆",
      "🥇",
      "🎯",
      "⚡",
      "🔥",
      "🚀",
      "💨",
      "🎰",
      "🎲",
      "💰",
    ],
  },
  {
    label: "Celebration",
    icon: "🎉",
    keywords: "party heart love star crown cheers",
    emojis: [
      "🎉",
      "🎊",
      "🥂",
      "🍾",
      "💛",
      "❤️",
      "💜",
      "💚",
      "⭐",
      "✨",
      "💥",
      "👑",
    ],
  },
] as const;

export function ChatComposer({
  disabled,
  disabledHint,
  error,
  replyTarget,
  sending,
  onCancelReply,
  onSend,
}: {
  disabled: boolean;
  disabledHint?: string;
  error?: string | null;
  replyTarget: ChatMessage | null;
  sending: boolean;
  onCancelReply: () => void;
  onSend: (text: string) => Promise<boolean>;
}) {
  const [value, setValue] = useState("");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiSearch, setEmojiSearch] = useState("");
  const [emojiGroup, setEmojiGroup] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const tooLong = value.length > MAX_CHAT_MESSAGE_LENGTH;

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    textarea.style.overflowY = textarea.scrollHeight > 120 ? "auto" : "hidden";
  }, [value]);

  useEffect(() => {
    if (!emojiOpen) return undefined;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      if (replyTarget) {
        onCancelReply();
      } else {
        setEmojiOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape, true);
    return () => document.removeEventListener("keydown", handleEscape, true);
  }, [emojiOpen, onCancelReply, replyTarget]);

  async function submit() {
    const text = value.trim();
    if (!text || disabled || tooLong || sending) return;
    if (await onSend(text)) {
      setValue("");
      setEmojiOpen(false);
    }
  }

  function insertEmoji(emoji: string) {
    const input = textareaRef.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    setValue(`${value.slice(0, start)}${emoji}${value.slice(end)}`);
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  }

  const query = emojiSearch.trim().toLowerCase();
  const visibleEmojis = query
    ? EMOJI_GROUPS.filter((group) =>
        `${group.label} ${group.keywords}`.toLowerCase().includes(query),
      ).flatMap((group) => group.emojis)
    : EMOJI_GROUPS[emojiGroup].emojis;

  return (
    <div className="chat-composer">
      {replyTarget ? (
        <div className="chat-composer__reply">
          <Reply aria-hidden />
          <span>
            <strong>Replying to {replyTarget.displayName}</strong>
            <small>{replyTarget.text}</small>
          </span>
          <button
            aria-label="Cancel reply"
            onClick={onCancelReply}
            type="button"
          >
            <X aria-hidden />
          </button>
        </div>
      ) : null}
      <div
        className={["chat-composer__field", disabled && "is-disabled"]
          .filter(Boolean)
          .join(" ")}
      >
        {emojiOpen ? (
          <div
            className="chat-composer__emoji"
            role="dialog"
            aria-label="Emoji picker"
          >
            <label className="chat-composer__emoji-search">
              <Search aria-hidden />
              <input
                aria-label="Search emoji"
                autoComplete="off"
                onChange={(event) => setEmojiSearch(event.target.value)}
                placeholder="Search emoji"
                type="search"
                value={emojiSearch}
              />
            </label>
            <div className="chat-composer__emoji-grid">
              {visibleEmojis.map((emoji, index) => (
                <button
                  aria-label={`Insert ${emoji}`}
                  key={`${emoji}-${index}`}
                  onClick={() => insertEmoji(emoji)}
                  type="button"
                >
                  {emoji}
                </button>
              ))}
            </div>
            <div className="chat-composer__emoji-tabs" role="tablist">
              {EMOJI_GROUPS.map((group, index) => (
                <button
                  aria-label={group.label}
                  aria-selected={emojiGroup === index}
                  className={emojiGroup === index ? "is-active" : ""}
                  key={group.label}
                  onClick={() => {
                    setEmojiGroup(index);
                    setEmojiSearch("");
                  }}
                  role="tab"
                  type="button"
                >
                  {group.icon}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <textarea
          aria-label="Type a message"
          disabled={disabled}
          maxLength={MAX_CHAT_MESSAGE_LENGTH + 100}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape" && replyTarget) {
              event.preventDefault();
              event.stopPropagation();
              onCancelReply();
              return;
            }
            if (
              event.key === "Enter" &&
              !event.shiftKey &&
              !event.ctrlKey &&
              !event.metaKey &&
              !event.altKey &&
              !window.matchMedia("(pointer: coarse)").matches
            ) {
              event.preventDefault();
              void submit();
            }
          }}
          placeholder="Type a message"
          ref={textareaRef}
          rows={1}
          value={value}
        />
        <button
          aria-expanded={emojiOpen}
          aria-label="Open emoji picker"
          disabled={disabled}
          onClick={() => setEmojiOpen((open) => !open)}
          type="button"
        >
          <Smile aria-hidden />
        </button>
        <button
          aria-label="Send"
          disabled={disabled || !value.trim() || tooLong || sending}
          onClick={() => void submit()}
          type="button"
        >
          <Send aria-hidden />
        </button>
      </div>
      {tooLong ? (
        <p className="chat-composer__error" role="alert">
          Message too long. Max {MAX_CHAT_MESSAGE_LENGTH} characters.
        </p>
      ) : error ? (
        <p className="chat-composer__error" role="alert">
          {error}
        </p>
      ) : disabledHint ? (
        <p className="chat-composer__hint">{disabledHint}</p>
      ) : null}
    </div>
  );
}
